import { Injectable } from '@angular/core';

import { AuthService, BaseFormService, ConfigService } from '../../../core';
import { ConfigPedido, isAmostra, isSimulacao, isOrcamento, isVenda, isConsignacao } from '../../configuracoes/app/pedido';
import { PedidoEmailer } from '../email';
import { PedidoReporter } from '../report';
import { PedidoDuplicater } from '../duplicate';
import { PedidoCancelar } from '../cancelamento';
import { Pedido } from '../pedido';
import { PedidoSearch } from '../pedido-search';
import { PedidoConfirmavel } from '../confirmar';
import { PedidoConfirmavelErp, PedidoIntegraterErp } from '../integration-erp';
import { PedidoEditavel } from '../form';
import { PedidoAmostravel } from '../pedido-amostravel';
import { isPedidoNaoConfirmado, isPedidoConfirmado, isPedidoConfirmandoERP, isPedidoEmAnalise, isPedidoAprovado, isPedidoNaoLiberado, isPedidoCancelado, isPedidoStatusOutro, isPedidoRevisao, isPedidoEmAnaliseCliente } from '../status';
import { isOrigemLancamentoCadastroSogivendas, isOrigemLancamentoCadastroEcommerce, isOrigemLancamentoCadastroErp } from '../../../models/origem-lancamento-cadastro';
import { isPedidoSetorGepComercial, isPedidoSetorGepPCP, isPedidoSetorGepExpedicao, isPedidoSetorGepAguardandoProducao } from '../gep/setor-gep';
import { UsuarioPerfil, isPerfilAdministrador, isPerfilAtendente, isPerfilGerente, isPerfilRepresentante, isPerfilExpedicao } from '../../usuario/usuario-perfil';
import { isFunilVendaEtapaTipoPersonalizada } from '../../crm/funil-venda/fase/etapa/funil-venda-etapa-tipo';
import { OperacaoVendaID, OperacaoContaOrdemID } from '../domain';
import { EnumSituacaoCliche } from '../enums/situacao-cliche';

/** Service que valida os acessos e execuções de funcionalidades dos Pedidos */
@Injectable({
    providedIn: 'root'
})
export class PedidoAuthGuardService {

    constructor(private authService: AuthService,
                private baseFormService: BaseFormService,
                private configService: ConfigService) { }

    /** Verifica se pode abrir o pedido */
    canOpen(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }

        // Se for um representante e o pedido não for dele, então não pode salvar
        if (this.baseFormService.isRepresentanteAndEntityNotRepresentante(entity)) {
            return false;
        }
        // Se for um gerente ou atendente e o pedido não for de um de seus representantes, então não pode salvar
        // if (this.baseFormService.isGerenteOuAtendenteAndEntityNotSeuRepresentante(entity.representante)) {
        //     return false;
        // }

        return true;
    }

    /** Verifica se pode iserir um novo pedido */
    canInsert(perfilUsuario: UsuarioPerfil): boolean {
        if (!perfilUsuario) {
            return false;
        }

        return isPerfilAdministrador(perfilUsuario.id) || isPerfilAtendente(perfilUsuario.id) || isPerfilGerente(perfilUsuario.id) || isPerfilRepresentante(perfilUsuario.id);
    }

    /** Verifica se pode realizar edições/alterações no pedido */
    canEdit(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        if (!entity) {
            return false;
        }
        // Se estiver inserindo, então pode alterar
        if (editMode != null && !editMode) {
            return true;
        }
        // Se for um pedido lançado no sistema ERP, então não pode salvar
        if (isOrigemLancamentoCadastroErp(entity.origemLancamento)) {
            return false;
        }
        // Se for um representante e o pedido não for dele, então não pode salvar
        if (this.baseFormService.isRepresentanteAndEntityNotRepresentante(entity)) {
            return false;
        }
        // Se for um gerente ou atendente e o pedido não for de um de seus representantes, então não pode salvar
        // if (this.baseFormService.isGerenteOuAtendenteAndEntityNotSeuRepresentante(entity.representante)) {
        //     return false;
        // }

        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            // Se não estiver em uma etapa personalizada do funil de vendas
            if (!isFunilVendaEtapaTipoPersonalizada(entity.tipoFunilVendaEtapaOportunidade)) {
                return false;
            }

            if (this.canRevisar(entity)) {
                return true;
            }

            // Em revisão e etapa não permite editar revisões
            if (isPedidoRevisao(entity.status) && !entity.podeEditarRevisaoOrcamento) {
                return false;
            }
        }

        // Setor GEP PCP
        if (isPedidoSetorGepPCP(entity.setorGep)) {
            return this.authService.isPCP();
        }

        // Não Liberado
        if (isPedidoNaoLiberado(entity.status)) {
            return this.authService.podeEditarPedidoNaoLiberado();
        }

        // Confirmado
        if (isPedidoConfirmado(entity.status)) {
            return this.authService.podeEditarPedidoConfirmado();
        }

        // Obtendo o perfil do usuário logado
        const perfilUsuario = this.authService.getPerfilUsuario();
        if (!perfilUsuario) {
            return false;
        }

        // Obtendo a config. dos pedidos
        const configPedido = this.configService.getPedido();
        if (!configPedido) {
            return false;
        }

        if (isPerfilAdministrador(perfilUsuario.id)) { // Se for um Administrador
            return isPedidoNaoConfirmado(entity.status) ||
                isPedidoConfirmado(entity.status) ||
                isPedidoNaoLiberado(entity.status) ||
                isPedidoRevisao(entity.status) ||
                (isPedidoConfirmandoERP(entity.status) && (configPedido.podeSalvarConfirmandoErp || isExportingOneErp)) ||
                (isPedidoEmAnalise(entity.status) && (configPedido.podeSalvarEmAnalise || isExportingOneErp)) ||
                (isPedidoAprovado(entity.status) && configPedido.podeSalvarAprovado);
        } else if (isPerfilRepresentante(perfilUsuario.id)) { // Se for um Representante
            return (!entity.status ||
                    isPedidoNaoConfirmado(entity.status) ||
                    isPedidoConfirmado(entity.status) ||
                    isPedidoNaoLiberado(entity.status) ||
                    isPedidoRevisao(entity.status)
                    ) &&
                    !entity.idCotacao;
        } else if (isPerfilAtendente(perfilUsuario.id)) { // Se for um Atendente
            return isPedidoNaoConfirmado(entity.status) ||
                isPedidoConfirmado(entity.status) ||
                isPedidoNaoLiberado(entity.status) ||
                isPedidoRevisao(entity.status) ||
                (isPedidoConfirmandoERP(entity.status) && (configPedido.podeSalvarConfirmandoErp || isExportingOneErp)) ||
                (isPedidoEmAnalise(entity.status) && (configPedido.podeSalvarEmAnalise || isExportingOneErp)) ||
                (isPedidoAprovado(entity.status) && configPedido.podeSalvarAprovado);
        } else if (isPerfilGerente(perfilUsuario.id)) { // Gerente
            return (
                isPedidoNaoConfirmado(entity.status) ||
                isPedidoConfirmado(entity.status) ||
                isPedidoNaoLiberado(entity.status) ||
                isPedidoRevisao(entity.status)
            ) && !entity.idCotacao;
        }

        return false;
    }

    /** Verifica se pode editar o tipo do pedido */
    canEditTipo(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar o pedido
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Se for uma edição
        if (editMode) {
            return false;
        }
        // Se for uma simulação
        if (isSimulacao(entity.tipoVenda)) {
            return false;
        }
        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode editar o status do pedido */
    canEditStatus(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar o pedido
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Se for uma simulação
        if (isSimulacao(entity.tipoVenda)) {
            return false;
        }

        // Obtendo o perfil do usuário logado
        const perfilUsuario = this.authService.getPerfilUsuario();
        if (!perfilUsuario) {
            return false;
        }
        // Se não for adminitrador e nem um atendente e nem um representante, então não pode alterar o status
        if (!isPerfilAdministrador(perfilUsuario.id) && !isPerfilGerente(perfilUsuario.id) && !isPerfilAtendente(perfilUsuario.id) && !isPerfilRepresentante(perfilUsuario.id)) {
            return false;
        }

        // Somente pode alterar o status do pedido quando o mesmo estiver com o status "Confirmado" ou "Não Confirmado"
        return !editMode || !entity.status || isPedidoConfirmado(entity.status) || isPedidoNaoConfirmado(entity.status);
    }

    /** Verifica se possui operação de venda */
    private hasOperacaoVenda(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }
        return isVenda(entity.tipoVenda) || isOrcamento(entity.tipoVenda) || isSimulacao(entity.tipoVenda);
    }

    /** Verifica se deve exibir as informações de operação de venda do pedido */
    canShowOperacaoVenda(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }
        // Somente deve exibir se for venda
        return this.hasOperacaoVenda(entity);
    }

    /** Verifica se permite editar a operação de venda do pedido */
    canEditOperacaoVenda(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Conforme estiver configurado na config. dos pedidos
        return this.configService.getPedido().podeEditarOperacaoVenda;
    }

    /** Verifica se deve exibir as informações de operação de conta e ordem do pedido */
    canShowOperacaoContaOrdem(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }
        // Se estiver configurado para não exibir conta e ordem na config. dos pedidos
        if (!this.configService.getPedido().exibirClienteContaOrdem) {
            return false;
        }
        // Somente se possuir operação de venda e se a operação de venda for normal
        return this.hasOperacaoVenda(entity) && OperacaoVendaID.isNormal(entity.operacaoVenda);
    }

    /** Verifica se permite editar a operação de conta e ordem do pedido */
    canEditOperacaoContaOrdem(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Conforme estiver configurado na config. dos pedidos
        return this.configService.getPedido().podeEditarOperacaoContaOrdem;
    }

    /** Verifica se deve exibir o cliente de conta e ordem */
    canShowClienteContaOrdem(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }
        // Se a operação de conta e ordem não deve ser exibida, então o cliente também não deve
        if (!this.canShowOperacaoContaOrdem(entity)) {
            return false;
        }
        // Se for sem operação de conta e ordem, não exibe o cliente
        if (!entity.operacaoContaOrdem || OperacaoContaOrdemID.isSemOperacao(entity.operacaoContaOrdem)) {
            return false;
        }
        return true;
    }

    /** Verifica se permite editar a finalidade do pedido */
    canEditFinalidade(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Conforme estiver configurado na config. dos pedidos
        return this.configService.getPedido().podeEditarFinalidade;
    }

    /** Verifica se pode editar o cliente do pedido */
    canEditCliente(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar o pedido
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Se for uma simulação
        if (isSimulacao(entity.tipoVenda)) {
            return false;
        }
        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode editar o cliente conta e ordem do pedido */
    canEditClienteContaOrdem(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar o pedido
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Se for uma simulação
        if (isSimulacao(entity.tipoVenda)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode editar a campanha do pedido */
    canEditCampanha(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar o pedido
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Se for uma simulação
        if (isSimulacao(entity.tipoVenda)) {
            return false;
        }
        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode editar o representante do pedido */
    canEditRepresentante(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar o pedido
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Se for uma simulação
        if (isSimulacao(entity.tipoVenda)) {
            return false;
        }

        return true;
    }

    /** Verifica se possui permissão para solicitar a revisão do pedido/orçamento */
    canSolicitarRevisaoOrcamento(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }
        // Se a etapa do funil de venda não permite solicitar revisão
        if (!entity.podeSolicitarRevisaoOrcamento) {
            return false;
        }

        // Somente se estiver no status permitidos
        return isPedidoNaoConfirmado(entity.status) ||
                isPedidoConfirmado(entity.status) ||
                isPedidoNaoLiberado(entity.status) ||
                isPedidoRevisao(entity.status) ||
                isPedidoEmAnaliseCliente(entity.status);
    }

    /** Verifica se possui permissão para revisar o pedido/orçamento */
    canRevisar(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }

        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            // Se não estiver em uma etapa personalizada do funil de vendas
            if (!isFunilVendaEtapaTipoPersonalizada(entity.tipoFunilVendaEtapaOportunidade)) {
                return false;
            }

            // Se a etapa não permitir revisar orçamento
            if (!entity.podeRevisarOrcamento) {
                return false;
            }
        }

        // Somente se estiver no status de revisão e o usuário possuir permissão
        return isPedidoRevisao(entity.status) && this.authService.podeRevisarPedidoOrcamento();
    }

    /** Verifica se possui permissão para confirmar os dados do PCP do pedido/orçamento */
    canConfirmarPCP(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }

        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            // Se não estiver em uma etapa personalizada do funil de vendas
            if (!isFunilVendaEtapaTipoPersonalizada(entity.tipoFunilVendaEtapaOportunidade)) {
                return false;
            }
        }

        // Somente se estiver no setor GEP do PCP e o usuário for do PCP
        return isPedidoSetorGepPCP(entity.setorGep) && this.authService.isPCP();
    }

    /** Verifica se pode exibir as simulações do pedido */
    canShowSimulacoes(entity: PedidoEditavel, editMode: boolean): boolean {
        // Somente se estiver editando o pedido
        if (!editMode) {
            return false;
        }
        // Se for uma simulação, não pode simular uma simulação
        if (isSimulacao(entity.tipoVenda)) {
            return false;
        }
        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            return false;
        }
        // Se o usuário não possuir permissão
        if (!this.authService.podeSimularPedido()) {
            return false;
        }

        return true;
    }

    /** Verifica se pode exibir as liberações do pedido */
    canShowLiberacoes(entity: Pedido): boolean {
        // Pedido não informado ou for um novo pedido
        if (!entity || !entity.id) {
            return false;
        }

        // Se estiver no status de Não Liberado
        return isPedidoNaoLiberado(entity.status);
    }

    /** Verifica se pode exibir as liberações do pedido */
    canShowLiberacoesHistoricas(entity: Pedido): boolean {
       // Pedido não informado ou for um novo pedido
        if (!entity || !entity.id) {
            return false;
        }

        // Usuário sem permissão de liberação de pedidos
        if (!this.authService.podeLiberarPedido()) {
            return false;
        }

        // Se possuir alguma liberação histórica
        if (entity.liberadoManualmenteBonificacaoExcedente ||
            entity.liberadoManualmentePercDescontosMaximosItensExcedidos ||
            entity.drp?.liberadoManualmenteLucroLiquidoAbaixo) {
                return true;
        }

        return false;
    }

    /** Verifica se pode editar as simulações do pedido */
    canEditSimulacoes(entity: PedidoEditavel): boolean {
        // Se for uma simulação, não pode simular uma simulação
        if (isSimulacao(entity.tipoVenda)) {
            return false;
        }
        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            return false;
        }
        // Se o usuário não possuir permissão
        if (!this.authService.podeSimularPedido()) {
            return false;
        }

        return true;
    }

    /** Verifica se pode aplicar as simulações do pedido */
    canApplySimulacoes(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar o pedido
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Se não puder editar a simulação
        if (!this.canEditSimulacoes(entity)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode realizar edições/alterações nas comissões do pedido */
    canEditComissao(entity: PedidoEditavel, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar o pedido
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        // Se não possuir permissão de editar as comissões
        if (!this.authService.podeEditarPedidoComissao()) {
            return false;
        }

        return true;
    }

    /** Verifica se pode exibir as entregas dos itens do pedido */
    canShowEntregas(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }
        // Obtendo a config. dos pedidos
        const configPedido = this.configService.getPedido();
        if (!configPedido) {
            return false;
        }

        // Se estiver confirmando os dados do PCP não exibe pois possui view específica para confirmação
        if (this.canConfirmarPCP(entity)) {
            return false;
        }

        // Conforme estiver configurado na config. dos pedidos e possui entregas nos pedidos
        return configPedido.possuiEntregas;
    }

    /** Verifica se pode realizar edições/alterações nas entregas dos itens do pedido */
    canEditEntregas(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }
        // Se não for um pedido lançado no Sogi, então não pode editar
        if (!isOrigemLancamentoCadastroSogivendas(entity.origemLancamento)) {
            return false;
        }

        // Se não puder editar as entregas
        if (!this.authService.podeEditarEntregas()) {
            return false;
        }

        if (this.authService.isAdministrador()) { // Se for um Administrador
            return isPedidoNaoConfirmado(entity.status) ||
                isPedidoConfirmado(entity.status) ||
                isPedidoNaoLiberado(entity.status);
        } else if (this.authService.isPCP()) { // Se for um usuário do PCP
            return isPedidoConfirmado(entity.status);
        }

        return false;
    }

    /** Verifica se pode editar a anotação das entregas dos itens do pedido */
    canEditAnotacaoEntregas(entity: PedidoEditavel): boolean {
         // Se não pouder nem editar as entregas
         if (!this.canEditEntregas(entity)) {
            return false;
         }
         // Se possuir cotação então não pode
         if (entity.idCotacao > 0) {
            return false;
         }
         return true;
    }

    /** Verifica se pode exibir a validade da proposta comercial do orçamento */
    canShowValidadePropostaComercial(entity: {tipoVenda: number}): boolean {
        // Somente exibe para os tipos de venda: Venda, Consignação e Orçamento
        return isVenda(entity.tipoVenda) || isConsignacao(entity.tipoVenda) || isOrcamento(entity.tipoVenda);
    }

    /** Verifica se a validade da proposta comercial é obrigatória */
    isValidadePropostaComercialRequired(entity: {tipoVenda: number}, clientePossuiValidadePropostaComercial: boolean): boolean {
        // Se não puder exibir a validade da proposta comercial, então não é obrigatório
        if (!this.canShowValidadePropostaComercial(entity)) {
            return false;
        }

        // Se estiver configurado para obrigar a validade da proposta comercial em todos os orçamentos/pedidos, ou se o cliente possuir validade de pedidos, então é obrigatório
        return this.configService.getPedido().obrigarValidadePropostaComercial || clientePossuiValidadePropostaComercial;
    }

    /** Verifica se pode informar a validade da proposta comercial do orçamento */
    canSetValidadePropostaComercial(entity: {tipoVenda: number}): boolean {
        // Se não puder exibir a validade da proposta comercial, então não pode informar a validade da proposta comercial
        if (!this.canShowValidadePropostaComercial(entity)) {
            return false;
        }
        // Somente permite informar a validade da proposta comercial para os usuários que possuem esta permissão
        return this.authService.podeInformarValidadePropostaComercialPedido();
   }

    /** Verifica se deve exibir as informações de pagamento do pedido */
    canShowPagamento(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }

        // Tentando obter a config. do tipo do pedido
        const configTipo = this.configService.getPedidoTipo(entity.tipoVenda);
        if (configTipo) { // Se encontrar, considera a permissão informada na config. do tipo
            return configTipo.exibirPagamento;
        }

        // Se não encontrar config. específica do tipo, então sempre exibe;
        return true;
    }

    /** Verifica se permite editar as informações de pagamento do pedido */
    canEditPagamento(entity: PedidoEditavel, clientePossuiPagamentoPadrao: boolean, isExportingOneErp: boolean, editMode: boolean): boolean {
        // Se não puder editar
        if (!this.canEdit(entity, isExportingOneErp, editMode)) {
            return false;
        }
        const configPedido = this.configService.getPedido();

        // Tentando obter a config. do tipo do pedido
        const configTipo = this.configService.getPedidoTipo(entity.tipoVenda);
        if (configTipo && !configTipo.podeEditarPagamento) { // Se encontrar, verifica se a config. específica do tipo não permite editar o pagamento
            return false;
        }

        // Se cliente possuir pagamento padrão e estiver configurado para não permitir editar o pagamento padrão do cliente, então não pode editar as informações de pagamento do pedido
        if (clientePossuiPagamentoPadrao && configPedido && !configPedido.podeEditarPagamentoPadraoCliente) {
            return false;
        }

        // Se não pode editar o pagamento
        return true;
    }

    /** Verifica se pode exibir o histórico das condições de pagamentos */
    canShowPagamentoHistorico(entity: PedidoEditavel, editMode: boolean): boolean {
        // Pedido não informado
        if (!entity) {
            return false;
        }
        // Se estiver inserindo o pedido, então pode exibir o histórico
        if (!editMode) {
            return true;
        }
        // Se o status do pedido não for a "Não Confirmado", "Confirmado" e "Nao Liberado" então não pode exibir o histórico
        if (!isPedidoNaoConfirmado(entity.status) && !isPedidoConfirmado(entity.status) && !isPedidoNaoLiberado(entity.status)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode enviar o pedido por e-mail */
    canSendEmail(entity: PedidoEmailer, funilVendaEtapaPodeImprimirEnviarOrcamento: boolean): boolean {
        // Se for um representante e o pedido não for dele, então não pode enviar o e-mail
        if (this.baseFormService.isRepresentanteAndEntityNotRepresentante(entity)) {
            return false;
        }

        // Se for um orçamento e a etapa do funil de venda não permitir
        if (isOrcamento(entity.tipoVenda) && !funilVendaEtapaPodeImprimirEnviarOrcamento) {
            return false;
        }
        // Se for um orçamento e o perfil do usuário não possuir permissão
        if (isOrcamento(entity.tipoVenda) && !this.authService.isAdministrador() && !this.authService.isAtendente() && !this.authService.isGerente() && !this.authService.isRepresentante()) {
            return false;
        }

        // Obtendo a config. dos pedidos
        const configPedido = this.configService.getPedido();
        if (!configPedido) {
            return false;
        }

        // Se estiver configurado para poder enviar e-mail somente quando o pedido estiver integrado e o pedido não estiver integrado e for um representante, então não pode enviar o e-mail
        if (configPedido.podeEnviarEmailSomenteIntegrado && !entity.integradoErp && this.authService.isRepresentante()) {
            return false;
        }

        return true;
    }

    /** Verifica se pode enviar a proposta comercial de um orçamento por e-mail */
    canSendEmailPropostaComercial(entity: PedidoEmailer): boolean {
        if (!entity) {
            return false;
        }

        // Somente se for um orçamento em análise do cliente
        return isOrcamento(entity.tipoVenda) && isPedidoEmAnaliseCliente(entity.status) && (this.authService.isAdministrador() || this.authService.isAtendente() || this.authService.isGerente() || this.authService.isRepresentante());
    }


    /** Verifica se pode imprimir o Pedido */
    canPrint(entity: PedidoReporter, funilVendaEtapaPodeImprimirEnviarOrcamento: boolean): boolean {
        if (!entity) {
            return false;
        }
        // Se for um orçamento e a etapa do funil de venda não permitir
        if (isOrcamento(entity.tipoVenda) && !funilVendaEtapaPodeImprimirEnviarOrcamento) {
            return false;
        }
        // Se for um orçamento e o perfil do usuário não possuir permissão
        if (isOrcamento(entity.tipoVenda) && !this.authService.isAdministrador() && !this.authService.isAtendente() && !this.authService.isGerente() && !this.authService.isRepresentante()) {
            return false;
        }

        // Obtendo a config. dos pedidos
        const configPedido = this.configService.getPedido();
        if (!configPedido) {
            return false;
        }

        // Se estiver configurado para poder imprimir somente quando o pedido estiver integrado e o pedido não estiver integrado e for um representante, então não pode imprimir
        if (configPedido.podeImprimirSomenteIntegrado && !entity.integradoErp && this.authService.isRepresentante()) {
            return false;
        }

        return true;
    }

    /** Verifica se pode imprimir a proposta comercial de um orçamento */
    canPrintPropostaComercial(entity: PedidoReporter): boolean {
        if (!entity) {
            return false;
        }

        // Somente se for um orçamento em análise do cliente
        return isOrcamento(entity.tipoVenda) && isPedidoEmAnaliseCliente(entity.status) && (this.authService.isAdministrador() || this.authService.isAtendente() || this.authService.isGerente() || this.authService.isRepresentante());
    }

    /** Verifica se pode liberar o pedido */
    canLiberar(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }

        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            // Se não estiver em uma etapa personalizada do funil de vendas
            if (!isFunilVendaEtapaTipoPersonalizada(entity.tipoFunilVendaEtapaOportunidade)) {
                return false;
            }
        }

        // Se o pedido não estiver no status "Não Liberado", então não pode liberar
        if (!isPedidoNaoLiberado(entity.status)) {
            return false;
        }

        // Somente se o usuário possui permissão para liberar pedidos
        return this.authService.podeLiberarPedido();
    }

    /** Verifica se possui permissão para retornar a análise de cliente do orçamento */
    canRetornarAnaliseCliente(entity: PedidoEditavel): boolean {
        if (!entity) {
            return false;
        }

        // Se for um orçamento
        if (isOrcamento(entity.tipoVenda)) {
            // Se não estiver em uma etapa personalizada do funil de vendas
            if (!isFunilVendaEtapaTipoPersonalizada(entity.tipoFunilVendaEtapaOportunidade)) {
                return false;
            }
        }

        // Somente se for um orçamento em análise do cliente e o perfil de usuário possuir permissão
        return isOrcamento(entity.tipoVenda) && isPedidoEmAnaliseCliente(entity.status) && (this.authService.isAdministrador() || this.authService.isAtendente() || this.authService.isGerente() || this.authService.isRepresentante());
    }

    /** Verifica se pode iniciar o aguardando produção do pedido */
    canIniciarAguardandoProducao(entity: PedidoSearch, perfilUsuario: UsuarioPerfil): boolean {
        if (!entity) {
            return false;
        }

        // Se não for o Administrador, Atendente ou Expedição, então não pode iniciar
        if (!isPerfilAdministrador(perfilUsuario.id) && !isPerfilAtendente(perfilUsuario.id) && !isPerfilExpedicao(perfilUsuario.id)) {
            return false;
        }

        // Se não for o status Aprovado
        if (!isPedidoAprovado(entity.status)) {
            return false;
        }

        // Somente no setor GEP Comercial ou Expedição
        return isPedidoSetorGepComercial(entity.setorGep) || isPedidoSetorGepExpedicao(entity.setorGep);
    }

    /** Verifica se pode terminar o aguardando produção do pedido */
    canTerminarAguardandoProducao(entity: PedidoSearch, perfilUsuario: UsuarioPerfil): boolean {
        if (!entity) {
            return false;
        }

        // Se não for o Administrador, Atendente ou Expedição, então não pode terminar
        if (!isPerfilAdministrador(perfilUsuario.id) && !isPerfilAtendente(perfilUsuario.id) && !isPerfilExpedicao(perfilUsuario.id)) {
            return false;
        }

        // Se não for o status Aprovado
        if (!isPedidoAprovado(entity.status)) {
            return false;
        }

        // Somente no setor GEP Aguardando Produção
        return isPedidoSetorGepAguardandoProducao(entity.setorGep);
    }

    /** Verifica se pode liberar o Pedido para expedição */
    canLiberarExpedicao(entity: PedidoSearch, isAdministrador: boolean, isAtendente: boolean): boolean {
        if (!entity) {
            return false;
        }

        // Se não for o Administrador e nem o Atendente, então não pode liberar para expedição
        if (!isAdministrador && !isAtendente) {
            return false;
        }

        // Somente pedido Aprovado com o setor GEP Comercial pode ser liberado para expedição
        return isPedidoAprovado(entity.status) && isPedidoSetorGepComercial(entity.setorGep);
    }

    /** Verifica se pode cancelar o Pedido */
    canCancelar(entity: PedidoCancelar, isAdministrador: boolean, isAtendente: boolean, isRepresentante: boolean): boolean {
        // Se for um representante e o pedido não for dele, então não pode cancelar
        if (this.baseFormService.isRepresentanteAndEntityNotRepresentante(entity)) {
            return false;
        }
        // Se for um gerente ou atendente e o pedido não for de um de seus representantes, então não pode cancelar
        // if (this.baseFormService.isGerenteOuAtendenteAndEntityNotSeuRepresentante(entity ? entity.representante : null)) {
        //     return false;
        // }

        // Se for um pedido gerado através de uma cotação, somente o administrador ou atendente é quem pode cancelar
        if (entity.idCotacao && !isAdministrador && !isAtendente) {
            return false;
        }
        // Se não for administrador, representante ou atendente, então não pode cancelar
        if (!isAdministrador && !isRepresentante && !isAtendente) {
            return false;
        }

        return isPedidoNaoConfirmado(entity.status) || isPedidoConfirmado(entity.status) || isPedidoNaoLiberado(entity.status);
    }

    /** Verifica se pode duplicar o pedido */
    canDuplicate(entity: PedidoDuplicater): boolean {
        if (!entity) {
            return false;
        }
        // Somente pode duplicar pedidos lançados no Sogivendas
        if (!isOrigemLancamentoCadastroSogivendas(entity.origemLancamento)) {
            return false;
        }
        // Se for um pedido gerado através de uma cotação não pode duplicar
        if (entity.idCotacao) {
            return false;
        }
        // Se o pedido não possuir um ID, não pode duplicar
        if (!entity.id || isNaN(entity.id) || entity.id <= 0) {
            return false;
        }

        return true;
    }

    /** Verifica se poder realizar a confirmação do pedido */
    canConfirmar(entity: PedidoConfirmavel): boolean {
        if (!entity) {
            return false;
        }
        // Se não possuir permissão para confirmar pedidos
        if (!this.authService.podeConfirmarPedido()) {
            return false;
        }
        // Se o status do pedido não for igual a Não Confirmado, então não pode confirmar
        if (!isPedidoNaoConfirmado(entity.status)) {
            return false;
        }
        // Se o pedido tiver sido lançado no ERP, então não pode confirmar
        if (isOrigemLancamentoCadastroErp(entity.origemLancamento)) {
            return false;
        }
        // Se for um representante e o pedido não for dele, então não pode confirmar
        if (this.baseFormService.isRepresentanteAndEntityNotRepresentante(entity)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode exportar o pedido para o ERP */
    canExportOneErp(entity: PedidoIntegraterErp, isExportingOneErp: boolean, configPedido: ConfigPedido): boolean {
        if (!entity) {
            return false;
        }
        // Se o pedido tiver sido lançado no ERP não pode enviar ele para o ERP
        if (isOrigemLancamentoCadastroErp(entity.origemLancamento)) {
            return false;
        }

        // Se não puder exportar para ERP
        if (!this.authService.podeExportarErp()) {
            return false;
        }

        // Se o pedido estiver "Confirmado" e estiver sendo confirmado para o ERP então pode integrar
        if (isPedidoConfirmado(entity.status) && isExportingOneErp) {
            return true;
        }
        // Se o Pedido estiver "Confirmando ERP" ou "Em Análise" e ainda não estiver sido integrado, então, pode integrar
        if ((isPedidoConfirmandoERP(entity.status) || isPedidoEmAnalise(entity.status)) && !entity.integradoErp) {
            return true;
        }
        // Se o Pedido estiver "Confirmando ERP" ou "Em Análise" e estiver sendo confirmado para o ERP, então, pode integrar
        if ((isPedidoConfirmandoERP(entity.status) || isPedidoEmAnalise(entity.status)) && isExportingOneErp) {
            return true;
        }
        // Se o Pedido estiver "Confirmando ERP" e puder salvar em "Confirmando ERP", então, pode integrar
        if (isPedidoConfirmandoERP(entity.status) && configPedido.podeSalvarConfirmandoErp) {
            return true;
        }
        // Se o Pedido estiver "Em Análise" e puder salvar "Em Análise", então, pode integrar
        if (isPedidoEmAnalise(entity.status) && configPedido.podeSalvarEmAnalise) {
            return true;
        }
        // Se o Pedido estiver "Aprovado" e puder salvar "Aprovado", então, pode integrar
        if (isPedidoAprovado(entity.status) && configPedido.podeSalvarAprovado) {
            return true;
        }

        return false;
    }

    /** Verifica se pode abrir o retorno das amostra do pedido informado */
    canOpenRetornoAmostras(entity: PedidoAmostravel, configPedido: ConfigPedido): boolean {
        // Se não puder abrir o pedido, então não pode abrir o retorno das amostras
        if (!this.canOpen(entity)) {
            return false;
        }

        // Se a empresa não valida as amostras, então não pode abrir
        if (!configPedido?.validarAmostra) {
            return false;
        }

        // Se não for um pedido de amostra, entõa não pode abrir
        if (!isAmostra(entity?.tipoVenda)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode executar a importação e sincronização do pedido com o e-commerce */
    canImportOneEcommerce(entity: PedidoSearch): boolean {
        if (!entity) {
            return false;
        }

        // Se não for um pedido de origem do e-commerce
        if (!isOrigemLancamentoCadastroEcommerce(entity.origemLancamento)) {
            return false;
        }

        return this.authService.podeImportarEcommerce();
    }

    /** Verifica se pode executar a integração de leitura individual do pedido com o ERP */
    canImportOneErp_v1(entity: PedidoSearch, isIntegracaoErpV1: boolean, isAdministrador: boolean, isAtendente: boolean): boolean {
        if (!entity) {
            return false;
        }
        // Somente se for a versão 1
        if (!isIntegracaoErpV1) {
            return false;
        }
        // Se o pedido ainda não estiver integrado, então não pode importar do ERP
        if (!entity.integradoErp) {
            return false;
        }
        // Se não for administrador ou atendente, então não pode importar do ERP
        if (!isAdministrador && !isAtendente) {
            return false;
        }

        return true;
    }

    /** Verifica se pode executar a importação e sincronização do pedido com o ERP */
    canImportOneErp(entity: PedidoSearch, isIntegracaoErpV1: boolean): boolean {
        if (!entity) {
            return false;
        }

        // Somente se não for a versão 1
        if (isIntegracaoErpV1) {
            return false;
        }

        // Somente se estiver integrado com o ERP
        if (!entity.idIntegracaoErp) {
            return false;
        }

        return this.authService.podeImportarErp();
    }

    /** Verifica se pode exibir o QV (Qualidade de Valor) do pedido */
    canShowQV(tipoVendaPedido: number): boolean {
        // Somente para pedidos de venda, simulação e orçamento
        if (!isVenda(tipoVendaPedido) && !isSimulacao(tipoVendaPedido) && !isOrcamento(tipoVendaPedido)) {
            return false;
        }
        // Somente se o usuário possuir permissão ao nível de visualização de QV ou nível superior
        return this.authService.podeVisualizarQV();
    }

    /** Verifica se pode exibir o D.R.P do pedido */
    canShowDRP(tipoVendaPedido: number): boolean {
        // Somente para pedidos de venda, simulação e orçamento
        if (!isVenda(tipoVendaPedido) && !isSimulacao(tipoVendaPedido) && !isOrcamento(tipoVendaPedido)) {
            return false;
        }
        // Somente se o usuário possuir permissão ao nível de visualização de D.R.P ou nível superior
        return this.authService.podeVisualizarDRP();
    }

    /** Verifica se pode exibir o D.R.P com todos os custos do pedido */
    canShowDRPCustos(tipoVendaPedido: number): boolean {
        // Somente para pedidos de venda, simulação e orçamento
        if (!isVenda(tipoVendaPedido) && !isSimulacao(tipoVendaPedido) && !isOrcamento(tipoVendaPedido)) {
            return false;
        }
        // Somente se o usuário possuir permissão ao nível de visualização de D.R.P + Custos
        return this.authService.podeVisualizarDRPCustos();
    }

    /** Verifica se pode exibir a simulação do resultado líquido do pedido */
    canShowSimulacaoResultadoLiquido(tipoVendaPedido: number, statusPedido: number): boolean {
        // Se não puder exibir o resultado líquido então também não pode exibir sua simulação
        if (!this.canShowDRP(tipoVendaPedido)) {
            return false;
        }

        // Se for um pedido cancelado ou com o status igual a Outro, então não exibe a simulação do resultado líquido
        if (isPedidoCancelado(statusPedido) || isPedidoStatusOutro(statusPedido)) {
            return false;
        }

        return true;
    }

    /** Verifica se pode exibir o local de faturamento do pedido */
    canShowLocalFaturamento(): boolean {
        return this.configService.getEmpresa().possuiLocalFaturamento ?? false;
    }

    /** Verifica se pode exibir a clicheria do pedido */
    canShowClicheria(): boolean {
        // Somente se a empresa possuir clicheria
        return this.configService.getEmpresa().possuiClicheria ?? false;
    }

    /** Verifica se pode exibir a observação do clichê */
    canShowObservacaoCliche(situacaoCliche: EnumSituacaoCliche): boolean {
        // Somente se a situação do clichê for com alteração ou novo
        return situacaoCliche == EnumSituacaoCliche.COM_ALTERACAO || situacaoCliche == EnumSituacaoCliche.NOVO;
    }
}

